import React, { useEffect, useState, Suspense } from 'react';
import { Form, Card, Col, Row, Select, DatePicker } from 'antd';
import { getStores, getSlotDates } from 'store/requests/definitions';
import {
  getZones,
  getCourierDashboardInfo,
  getCourierGpsData,
} from 'store/requests/courier';
import FormItem from 'antd/es/form/FormItem';
import AllCouriers from './AllCouriers/AllCouriers';
import { useTranslation } from 'react-i18next';
import { randomHexColorGenerator } from '../../../helpers/utils';
import { Loading } from '../../../components';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';

const SlotCouriersDetails = React.lazy(
  () => import('./SlotCouriers/SlotCouriersDetails'),
);

const { Option } = Select;
const CourierPerformanceDashboard = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const [storeOptions, setStoreOptions] = useState(null);
  const [zones, setZones] = useState(null);
  const [slotDates, setSlotDates] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [gpsData, setGpsData] = useState([]);
  const [filteredCouriers, setFilteredCouriers] = useState(null);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [selectedStoreLatLon, setSelectedStoreLatLon] = useState(null);
  const [form] = Form.useForm();
  const selectedStoreInput = Form.useWatch('store', form);
  const selectedZoneInput = Form.useWatch('zone', form);
  const selectedSlotInput = Form.useWatch('slot', form);
  const userStores = useSelector((state) => state.definitions?.userStores);
  const userRegions = useSelector((state) => state.definitions?.userRegions);
  const activeStoreId = useSelector(
    (state) => state.auth?.user.activeStore?.id,
  );
  const activeRegionId = useSelector(
    (state) => state.auth?.user.activeRegion?.id,
  );
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    if (userStores?.length) {
      const storesOptions = userStores?.map((store, index) => {
        return { label: `${store.code} - ${store.name}`, value: store.id };
      });
      setStoreOptions(storesOptions);
      if (activeStoreId) {
        form.setFieldsValue({ store: activeStoreId });
        getZones({
          queryString: `filter=store.id==${activeStoreId} and active==true`,
        }).then((res) => setZones(res));
      }
    } else if (userRegions?.length) {
      let queryString = `filter=region.id==${activeRegionId}`;
      getStores(queryString).then((res) => {
        setStoreOptions(res._embedded.storeResponseDtoList);
      });
    }
  }, [userStores, userRegions]);

  const slotDatesOptions = slotDates?.map((item) => {
    const slot = item?.slot;
    return { label: `${slot.startTime} - ${slot.endTime}`, value: slot.id };
  });

  const onChangeStore = (value) => {
    form.resetFields(['zone', 'date', 'slot']);
    let queryFilter = `store.id==${value}`;
    getZones({ queryString: `filter=${queryFilter} and active==true` }).then(
      (res) => {
        setZones(res);
      },
    );
  };

  const dateChangeHandler = (date, dateString) => {
    setSelectedCourier(null);
    setSelectedSlot(null);
    form.resetFields(['slot']);
    setSelectedDate(dateString);
    if (dateString) {
      const queryString = `?filter=slot.zone.id==${selectedZoneInput} and date==${dateString} and slot.channel==TRANSFORMATION and slot.active==true and active==true`;
      getSlotDates(queryString).then((data) => {
        setSlotDates(data?._embedded?.slotDateResponseDtoList ?? []);
      });
    }
  };

  useEffect(() => {
    if (selectedDate && selectedSlot) {
      const [year, month, day] = selectedDate?.split('-');

      const [startHour, startMinute] = selectedSlot?.split('-')[0].split(':');
      const [endHour, endMinute] = selectedSlot?.split('-')[1].split(':');

      const startDate = new Date(year, month - 1, day, startHour, startMinute);
      const endDate = new Date(year, month - 1, day, endHour, endMinute);

      const timeZoneOffset = 180;
      startDate.setMinutes(startDate.getMinutes() + timeZoneOffset);
      endDate.setMinutes(endDate.getMinutes() + timeZoneOffset);

      const isoStartDate = startDate.toISOString();
      const isoEndDate = endDate.toISOString();

      if (isoStartDate && isoEndDate && selectedCourier) {
        getCourierGpsData({
          phoneNumber: selectedCourier.phoneNumber,
          startDate: isoStartDate,
          endDate: isoEndDate,
        }).then((res) => {
          const lineCoordinates = res.coordinates.map((coord) => ({
            lat: coord.lat,
            lng: coord.lon,
          }));
          setGpsData(lineCoordinates);
        });
      }
    }
  }, [selectedSlot, selectedDate, selectedCourier]);

  useEffect(() => {
    if (selectedSlotInput) {
      const requestDto = {
        slotId: selectedSlotInput,
        date: selectedDate,
      };
      setIsFetching(true);
      const fetchData = () => {
        getCourierDashboardInfo(requestDto)
          .then((res) => {
            const couriersWithColorAssignment = res?.data?.map((courier) => {
              return { ...courier, hexColor: randomHexColorGenerator() };
            });
            setFilteredCouriers(couriersWithColorAssignment);
            setSelectedCourier(couriersWithColorAssignment[0]);
          })
          .catch(() => {
            setFilteredCouriers(null);
          })
          .finally(() => setIsFetching(false));
      };
      fetchData();
      const id = setInterval(fetchData, 120000);
      setIntervalId(id);
    } else {
      setFilteredCouriers(null);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [selectedSlotInput]);

  useEffect(() => {
    const selectedZone = zones?.find((zone) => zone.id === +selectedZoneInput);
    setSelectedStoreLatLon({
      lat: selectedZone?.store?.latitude,
      lng: selectedZone?.store?.longitude,
    });
  }, [selectedZoneInput]);

  const slotDate = slotDates?.find(
    (item) => item?.slot.id === selectedSlotInput,
  )?.date;

  return (
    <div className="courierPerformanceDashboard">
      <Row gutter={8} style={{ position: 'relative' }}>
        <Col span={20} className="relatedCouriersAndMapWrapper">
          <Card className="relatedCouriersAndMap">
            <Form
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 24 }}
              name="basic"
              form={form}
            >
              <Row gutter={6}>
                <Col span={6}>
                  <FormItem name="store">
                    <Select
                      showSearch
                      placeholder="Mağaza seçiniz."
                      optionFilterProp="children"
                      onChange={onChangeStore}
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={storeOptions}
                    ></Select>
                  </FormItem>
                </Col>
                <Col span={6}>
                  <FormItem name="zone">
                    <Select
                      showSearch
                      disabled={!selectedStoreInput}
                      placeholder={t('selectZone')}
                      optionFilterProp="children"
                    >
                      {zones?.map((zone) => (
                        <Option key={zone.id}>{zone.name}</Option>
                      ))}
                    </Select>
                  </FormItem>
                </Col>
                <Col span={6}>
                  <FormItem name="date">
                    <DatePicker
                      placeholder={t('pickDate')}
                      format={'YYYY-MM-DD'}
                      disabled={!selectedZoneInput}
                      style={{ width: '100%' }}
                      onChange={dateChangeHandler}
                    />
                  </FormItem>
                </Col>
                <Col span={6}>
                  <FormItem name="slot">
                    <Select
                      placeholder={t('slotTime')}
                      disabled={!selectedDate}
                      style={{ width: '100%' }}
                      options={slotDatesOptions?.sort(
                        (a, b) => +a.label.slice(0, 2) - +b.label.slice(0, 2),
                      )}
                      onChange={(selectedId) => {
                        const selectedOption = slotDatesOptions.find(
                          (option) => option.value === selectedId,
                        );

                        if (selectedOption) {
                          const selectedLabel = selectedOption.label;
                          setSelectedSlot(selectedLabel);
                        }
                      }}
                    />
                  </FormItem>
                </Col>
              </Row>
            </Form>
            {isFetching && (
              <Loading
                style={{
                  position: 'absolute',
                  zIndex: 1,
                  backgroundColor: '#ffffff7d',
                }}
              />
            )}
            {selectedZoneInput && (
              <>
                <Row>
                  {filteredCouriers?.length ? (
                    <p>
                      <strong>{slotDate}:</strong>
                    </p>
                  ) : (
                    <p>{t('pleaseSelectSlot')}</p>
                  )}
                </Row>
                <Row gutter={12} style={{ height: '76vh' }}>
                  <Suspense fallback={() => {}}>
                    <SlotCouriersDetails
                      selectedCourier={selectedCourier}
                      couriersList={filteredCouriers}
                      onClickCourier={(courier) => setSelectedCourier(courier)}
                      selectedZoneStoreLatLon={selectedStoreLatLon}
                      gpsData={gpsData}
                    />
                  </Suspense>
                </Row>
              </>
            )}
          </Card>
        </Col>
        {selectedZoneInput && (
          <Col span={4} className="allCouriersWrapper">
            <Card className="allCouriers">
              <AllCouriers zone={selectedZoneInput} />
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default CourierPerformanceDashboard;
